// Loan Compliance Module v1.0 PR 3 — capture a new MaterialEvent on the
// loan. POSTs to /api/compliance/track1/legal/loans/{id}/material-events.

import { useState } from "react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import type {
  CaptureMaterialEventRequest,
  ComplianceSeverity,
  MaterialEventSource,
  MaterialEventType,
} from "@/api/compliance/types";
import { useCaptureMaterialEvent } from "@/hooks/compliance/useCompliance";
import {
  MATERIAL_EVENT_SOURCE_LABELS,
  MATERIAL_EVENT_TYPE_LABELS,
} from "./labels";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

interface CaptureMaterialEventModalProps {
  loanId: string;
  open: boolean;
  onClose: () => void;
}

const EVENT_TYPES: MaterialEventType[] = [
  "Litigation",
  "MaterialAdverseChange",
  "KeyOfficerDeparture",
  "ChangeOfControl",
  "CybersecurityIncident",
  "GovernmentInvestigation",
  "Judgment",
  "MaterialCustomerLoss",
  "NameOrStructureChange",
  "EntityStatusChange",
  "Other",
];

const SOURCES: MaterialEventSource[] = [
  "BorrowerDisclosure",
  "ServicerRecord",
  "ComplianceDetected",
  "LoanEventLinked",
  "ThirdPartyNotice",
  "Other",
];

const SEVERITIES: ComplianceSeverity[] = [
  "Critical",
  "Material",
  "Process",
  "Observation",
];

function isoToday(tz: string) {
  return isoDateInTz(new Date(), tz);
}

export function CaptureMaterialEventModal({
  loanId,
  open,
  onClose,
}: CaptureMaterialEventModalProps) {
  const toast = useToast();
  const capture = useCaptureMaterialEvent(loanId);
  const tz = useUserTimeZone();

  const [source, setSource] = useState<MaterialEventSource>("BorrowerDisclosure");
  const [eventType, setEventType] = useState<MaterialEventType>("Litigation");
  const [severity, setSeverity] = useState<ComplianceSeverity>("Material");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dateDetected, setDateDetected] = useState(isoToday(tz));
  const [dateReported, setDateReported] = useState("");
  const [noticeObligation, setNoticeObligation] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    if (!description.trim()) {
      setError("Description is required.");
      return;
    }
    const body: CaptureMaterialEventRequest = {
      source,
      eventType,
      severity,
      title: title.trim(),
      description: description.trim(),
      dateDetected,
      dateReportedByBorrower: dateReported || null,
      noticeObligation,
    };
    try {
      await capture.mutateAsync(body);
      toast.success("Material event captured.");
      onClose();
      setTitle("");
      setDescription("");
      setDateReported("");
      setNoticeObligation(false);
    } catch (err) {
      const msg =
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not capture material event.";
      setError(msg);
      toast.error(msg);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Capture material event"
      description="BLA §12.4 / §4.5 — record a new material event for this loan."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={capture.isPending}
            data-testid="capture-material-event-submit"
          >
            {capture.isPending ? "Saving…" : "Capture event"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <div className="grid grid-cols-2 gap-space-2">
          <label className="flex flex-col gap-space-1 text-caption">
            <span className="text-gray-mid uppercase tracking-label">
              Event type
            </span>
            <select
              className="h-9 px-space-2 border border-border-light rounded-md text-small bg-white-pure"
              value={eventType}
              onChange={(e) => setEventType(e.target.value as MaterialEventType)}
              data-testid="material-event-type"
            >
              {EVENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {MATERIAL_EVENT_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-space-1 text-caption">
            <span className="text-gray-mid uppercase tracking-label">
              Source
            </span>
            <select
              className="h-9 px-space-2 border border-border-light rounded-md text-small bg-white-pure"
              value={source}
              onChange={(e) => setSource(e.target.value as MaterialEventSource)}
            >
              {SOURCES.map((s) => (
                <option key={s} value={s}>
                  {MATERIAL_EVENT_SOURCE_LABELS[s]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-space-1 text-caption">
            <span className="text-gray-mid uppercase tracking-label">
              Severity
            </span>
            <select
              className="h-9 px-space-2 border border-border-light rounded-md text-small bg-white-pure"
              value={severity}
              onChange={(e) => setSeverity(e.target.value as ComplianceSeverity)}
            >
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-space-1 text-caption">
            <span className="text-gray-mid uppercase tracking-label">
              Date detected
            </span>
            <input
              type="date"
              className="h-9 px-space-2 border border-border-light rounded-md text-small"
              value={dateDetected}
              onChange={(e) => setDateDetected(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-space-1 text-caption">
            <span className="text-gray-mid uppercase tracking-label">
              Date reported by borrower
            </span>
            <input
              type="date"
              className="h-9 px-space-2 border border-border-light rounded-md text-small"
              value={dateReported}
              onChange={(e) => setDateReported(e.target.value)}
            />
          </label>
          <label className="flex items-center gap-space-2 text-small mt-space-3">
            <input
              type="checkbox"
              checked={noticeObligation}
              onChange={(e) => setNoticeObligation(e.target.checked)}
              data-testid="material-event-notice-obligation"
            />
            Notice obligation
          </label>
        </div>

        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Title</span>
          <input
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="material-event-title"
          />
        </label>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">
            Description
          </span>
          <textarea
            className="min-h-[80px] px-space-2 py-space-1 border border-border-light rounded-md text-small"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="material-event-description"
          />
        </label>

        {error && (
          <p className="text-small text-status-error m-0" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
